/**
 * Übersicht — die Startseite der Prüfung.
 *
 * In fünf Sekunden beantworten: Was ist das hier, wo stehe ich, was mache
 * ich als Nächstes? Deshalb oben die Begrüßung mit dem Objekt, dann der
 * Fortschritt über die fünf Prüfbereiche, dann die Bereiche selbst als
 * Karten mit je einem Knopf, und erst darunter Zahlen und Verlauf.
 *
 * Die fünf Bereiche sind die Checkliste der Buchprüfung. Alles, was die
 * Anwendung sonst kann — Belege, Journal, Kontoauszug — hängt an Schritt 3.
 */

import { Fortschritt } from '../komponenten/Fortschritt';
import { datum, tageBis } from '../lib/api';
import type { Dokument, Kategorie, Kennzahlen, Kommentar, Pruefung, Schritt } from '../lib/typen';
import { SchrittKarten } from './beirat/SchrittKarten';

export interface UebersichtProps {
  pruefung: Pruefung | null;
  name: string;
  hausverwaltung: string | null;
  kennzahlen: Kennzahlen | null;
  schritte: Schritt[];
  kategorien: Kategorie[];
  dokumente: Dokument[];
  kommentare: Kommentar[];
  istBeirat: boolean;
  onSchritt: (nummer: number) => void;
  onUnterlagen: () => void;
  onRueckfragen: () => void;
  onProtokoll: () => void;
}

export function Uebersicht({
  pruefung,
  name,
  hausverwaltung,
  kennzahlen,
  schritte,
  kategorien,
  dokumente,
  kommentare,
  istBeirat,
  onSchritt,
  onUnterlagen,
  onRueckfragen,
  onProtokoll,
}: UebersichtProps) {
  const erledigt = schritte.filter((s) => s.status === 'erledigt').length;
  const offeneRueckfragen = kommentare.filter((k) => !k.eltern_id && k.status === 'offen').length;
  const restTage = tageBis(pruefung?.frist ?? null);
  const abgeschlossen = pruefung?.status === 'abgeschlossen';
  const naechster = schritte.find((s) => s.status === 'laufend') ?? schritte.find((s) => s.status !== 'erledigt');

  const rueckfragenJeSchritt: Record<number, number> = {};
  const katNach = new Map(kategorien.map((k) => [k.id, k]));
  const dokNach = new Map(dokumente.map((d) => [d.id, d]));
  for (const k of kommentare) {
    if (k.eltern_id || k.status !== 'offen') continue;
    let nr: number | null = null;
    if (k.schritt) nr = k.schritt;
    else if (k.dokument_id) nr = katNach.get(dokNach.get(k.dokument_id)?.kategorieId ?? '')?.schritt ?? null;
    else if (k.beleg_id || k.transaktion_id) nr = 3;
    if (nr) rueckfragenJeSchritt[nr] = (rueckfragenJeSchritt[nr] ?? 0) + 1;
  }

  const aktivitaeten = verlauf(schritte, dokumente, kommentare).slice(0, 5);
  const vorname = name.split(' ')[0] || name;

  return (
    <div className="wrap">
      {istBeirat && pruefung?.status === 'vorbereitung' && (
        <div className="warnband" style={{ marginBottom: 14 }}>
          <span>
            Die Prüfung ist noch nicht freigegeben — die Hausverwaltung stellt die Unterlagen zusammen. Sie können sich
            bereits einen Überblick verschaffen.
          </span>
        </div>
      )}

      <div className="gruss">
        <h1>Guten Tag{vorname ? `, ${vorname}` : ''},</h1>
        <p className="s">
          hier prüfen Sie die Jahresabrechnung {pruefung?.periode_von.slice(0, 4)} der{' '}
          <b>{pruefung?.liegenschaft_bez}</b> in fünf Schritten — mit den Unterlagen und Belegen, die{' '}
          {hausverwaltung ?? 'die Hausverwaltung'} bereitgestellt hat. Jeder Schritt sagt, worauf zu achten ist, und wird
          mit einem Haken abgeschlossen.
        </p>
      </div>

      <div className="card uebersicht-kopf">
        <div style={{ flex: 1, minWidth: 260 }}>
          <div className="eyebrow">{pruefung?.liegenschaft_bez}</div>
          <h2 style={{ margin: '2px 0 10px' }}>
            {pruefung?.bezeichnung} · {datum(pruefung?.periode_von)} – {datum(pruefung?.periode_bis)}
          </h2>
          <Fortschritt anteil={kennzahlen?.fortschritt ?? erledigt / Math.max(1, schritte.length)} text={`${erledigt} von ${schritte.length} Prüfbereichen abgeschlossen`} gross />
        </div>
        <div className="uebersicht-aktion">
          {abgeschlossen ? (
            <>
              <span className="pill ok">
                <span className="dot" />
                Prüfung abgeschlossen
              </span>
              <button className="btn" onClick={onProtokoll}>
                Zum Protokoll
              </button>
            </>
          ) : naechster ? (
            <button className="btn primary" onClick={() => onSchritt(naechster.nummer)}>
              {erledigt > 0 || naechster.status === 'laufend' ? 'Prüfung fortsetzen →' : 'Prüfung starten →'}
            </button>
          ) : (
            <button className="btn primary" onClick={onProtokoll}>
              Prüfung dokumentieren →
            </button>
          )}
          {pruefung?.status === 'freigegeben' && <span className="pill n"><span className="dot" />Prüfung freigegeben</span>}
        </div>
      </div>

      <div className="card statusband petrol">
        <Zahl wert={`${erledigt} / ${schritte.length}`} label="Prüfbereiche" />
        <Zahl wert={String(kennzahlen?.belegeGesamt ?? 0)} label="Buchungen mit Belegen" />
        <Zahl wert={String(kennzahlen?.dokumenteGesamt ?? 0)} label="Unterlagen" />
        <Zahl wert={String(offeneRueckfragen)} label="Offene Rückfragen" ton={offeneRueckfragen > 0 ? 'w' : undefined} />
        <Zahl
          wert={pruefung?.frist ? datum(pruefung.frist) : '—'}
          label={restTage !== null && restTage >= 0 && !abgeschlossen ? `Prüffrist · noch ${restTage} Tage` : 'Prüffrist'}
          ton={restTage !== null && restTage <= 7 && !abgeschlossen ? 'w' : undefined}
        />
        <div className="spacer" />
        <button className="btn statusband-knopf" onClick={onUnterlagen}>
          Alle Unterlagen →
        </button>
      </div>

      <div className="seitenkopf" style={{ marginTop: 22 }}>
        <h3 className="sec" style={{ margin: 0 }}>Prüfbereiche</h3>
      </div>
      <SchrittKarten
        schritte={schritte}
        kategorien={kategorien}
        dokumente={dokumente}
        belege={{ gesamt: kennzahlen?.belegeGesamt ?? 0, geprueft: kennzahlen?.belegeGeprueft ?? 0 }}
        rueckfragenJeSchritt={rueckfragenJeSchritt}
        onOeffnen={onSchritt}
        abgeschlossen={Boolean(abgeschlossen)}
      />

      <div className="uebersicht-unten">
        <div className="card" style={{ padding: 16 }}>
          <h3 style={{ margin: '0 0 10px', fontSize: 14 }}>Letzte Aktivitäten</h3>
          {aktivitaeten.length === 0 ? (
            <p className="s" style={{ margin: 0 }}>Noch keine Aktivitäten — die Prüfung beginnt mit Schritt 1.</p>
          ) : (
            <ul className="verlauf">
              {aktivitaeten.map((a, i) => (
                <li key={i}>
                  <span className="verlauf-zeit">{datum(a.zeit)}</span>
                  <span>{a.text}</span>
                </li>
              ))}
            </ul>
          )}
          {offeneRueckfragen > 0 && (
            <button className="btn ghost sm" style={{ marginTop: 10 }} onClick={onRueckfragen}>
              {offeneRueckfragen} offene Rückfrage{offeneRueckfragen === 1 ? '' : 'n'} ansehen →
            </button>
          )}
        </div>
        <div className="hinweisbox">
          <b>Gut zu wissen</b>
          <p>
            Ihre Prüfvermerke werden im Protokoll gesammelt und lassen sich jederzeit wieder öffnen. Rückfragen stellen
            Sie direkt am Beleg oder an der Unterlage; {hausverwaltung ?? 'die Hausverwaltung'} antwortet an derselben
            Stelle. Am Ende entsteht der Bericht des Beirats für die Eigentümerversammlung.
          </p>
        </div>
      </div>
    </div>
  );
}

function Zahl({ wert, label, ton }: { wert: string; label: string; ton?: 'w' | 'ok' }) {
  return (
    <div className="kpi">
      <div className={`v${ton ? ` ${ton}` : ''}`}>{wert}</div>
      <div className="l">{label}</div>
    </div>
  );
}

/** Die jüngsten Ereignisse, aus dem zusammengesetzt, was ohnehin geladen ist. */
function verlauf(schritte: Schritt[], dokumente: Dokument[], kommentare: Kommentar[]): { zeit: string; text: string }[] {
  const liste: { zeit: string; text: string }[] = [];
  for (const s of schritte) {
    if (s.erledigtAm) liste.push({ zeit: s.erledigtAm, text: `${s.erledigtVon ?? 'Beirat'} hat „${s.titel}" als geprüft markiert.` });
    else if (s.begonnenAm) liste.push({ zeit: s.begonnenAm, text: `„${s.titel}" begonnen.` });
  }
  for (const d of dokumente) {
    liste.push({ zeit: d.hochgeladenAm, text: `Die Verwaltung hat „${d.dateiname}" bereitgestellt.` });
    if (d.pruefstatusAm && d.pruefstatus === 'geprueft') liste.push({ zeit: d.pruefstatusAm, text: `${d.pruefstatusVon ?? 'Beirat'} hat „${d.dateiname}" geprüft.` });
  }
  for (const k of kommentare) {
    liste.push({
      zeit: k.erstellt_am,
      text: k.eltern_id
        ? `${k.autor_name} hat eine Rückfrage beantwortet.`
        : `${k.autor_name} hat eine Rückfrage gestellt: „${k.text.length > 70 ? `${k.text.slice(0, 70)}…` : k.text}"`,
    });
  }
  return liste.sort((a, b) => b.zeit.localeCompare(a.zeit));
}
